/**
 * A client-side mirror of `apps/api`'s `domain/flag-key.ts`.
 *
 * ## Why duplicate a rule instead of just letting the API reject it
 *
 * Because the API's rejection arrives *after* the operator has filled in five
 * more fields and pressed the button, and the field it names is the one field
 * they cannot change afterwards. Keys are never reused — defaults, overrides and
 * audit rows are all filed under the key — so a typo caught on submit is a
 * wasted flag that stays in the catalog forever, while a typo caught on the
 * third keystroke is nothing at all.
 *
 * The API still validates: this is a convenience, not a boundary, and the server
 * remains the only thing that decides. The two must agree, and the *shape* of
 * the rule is a cross-repo contract — keys authored here have to stay drop-in
 * compatible with VeevartBackend's resolver.
 *
 * @see apps/api/src/modules/feature-flags/domain/flag-key.ts — the authority
 */

/**
 * Dot-separated segments of lowercase alphanumerics, hyphens allowed inside a
 * segment. Matches every key VeevartBackend declares today, e.g.
 * `feature-flags-canary`, `log.debug.salesforce-sync.sf-to-aws`.
 */
const FLAG_KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*(?:\.[a-z0-9]+(?:-[a-z0-9]+)*)*$/;

/**
 * Mirrors the API's bound, which in turn matches VeevartBackend's
 * `FLAG_KEY_MAX_LENGTH`. Not a client-side nicety to relax: a key this form lets
 * through and the API refuses is the exact failure this file exists to prevent.
 */
export const MAX_FLAG_KEY_LENGTH = 128;

export type FlagKeyRejection =
    'empty' | 'too-long' | 'not-lowercase' | 'malformed' | 'leading-or-trailing-separator';

/** Classify a candidate key. Returns `null` when it is valid. */
export function rejectFlagKey(candidate: string): FlagKeyRejection | null {
    const value = candidate.trim();

    if (value.length === 0) return 'empty';
    if (value.length > MAX_FLAG_KEY_LENGTH) return 'too-long';

    // Checked before the pattern so casing — the most common near-miss — gets
    // the actionable message rather than the generic one.
    if (value !== value.toLowerCase()) return 'not-lowercase';

    if (/^[.-]|[.-]$/.test(value)) return 'leading-or-trailing-separator';
    if (!FLAG_KEY_PATTERN.test(value)) return 'malformed';

    return null;
}

/** The operator-facing explanation for a rejection. Mirrors the API's wording. */
export function explainFlagKeyRejection(rejection: FlagKeyRejection): string {
    switch (rejection) {
        case 'empty':
            return 'A key is required.';
        case 'too-long':
            return `Keys are limited to ${String(MAX_FLAG_KEY_LENGTH)} characters.`;
        case 'not-lowercase':
            return 'Keys are lowercase — `Checkout.NewFlow` and `checkout.new-flow` must not be two different flags.';
        case 'leading-or-trailing-separator':
            return 'A key cannot start or end with `.` or `-`.';
        case 'malformed':
            return 'Use lowercase letters, digits, `-` inside a segment and `.` between segments, e.g. `checkout.new-flow`.';
    }
}
